/**
 * React Query Client Configuration
 * 
 * Centralized configuration for React Query with authentication-aware settings.
 * Handles automatic token refresh, error handling, and cache management.
 */

import { QueryClient } from '@tanstack/react-query'
import { logger } from '../utils/logger'

/**
 * Create QueryClient with authentication-specific configuration
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry configuration
      retry: (failureCount, error: any) => {
        // Don't retry authentication errors
        if (error?.status === 401 || error?.status === 403) {
          logger.debug('Not retrying auth error', {
            component: 'QueryClient',
            status: error.status,
            failureCount
          })
          return false
        }
        
        // Retry other errors up to 3 times
        return failureCount < 3
      },
      
      // Cache configuration
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache when unused
      
      // Background refetch
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
      
      // Error handling
      throwOnError: (error: any) => {
        // Let authentication errors bubble up
        if (error?.status === 401 || error?.status === 403) {
          return true
        }
        
        // Handle other errors in components
        return false
      },
      
      // Network mode
      networkMode: 'online', // Only run queries when online
    },
    
    mutations: {
      // Retry mutations on network errors only
      retry: (failureCount, error: any) => {
        // Don't retry client errors (4xx)
        if (error?.status >= 400 && error?.status < 500) {
          return false
        }
        
        // Retry server errors (5xx) up to 2 times
        return failureCount < 2
      },
      
      // Network mode for mutations
      networkMode: 'online',
    },
  },
})

// Global error handler for queries
queryClient.setMutationDefaults(['auth'], {
  onError: (error: any) => {
    logger.error('Authentication mutation failed', {
      component: 'QueryClient',
      error: error.message,
      status: error.status
    })
  },
})

// Global success handler for auth mutations
queryClient.setMutationDefaults(['auth'], {
  onSuccess: (data: any, variables: any, context: any) => {
    logger.debug('Authentication mutation successful', {
      component: 'QueryClient',
      action: context?.action || 'unknown'
    })
  },
})